// @ts-nocheck
import { createClient } from '@supabase/supabase-js';
import { setSentryUser } from '../../sentry';



const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON;

console.log('[auth] VITE_SUPABASE_URL present?', !!supabaseUrl);
console.log('[auth] VITE_SUPABASE_ANON present?', !!supabaseAnonKey);



if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('[auth] Missing Supabase env vars. Login will not work.');
}

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '');

/* === SESSION BRIDGE (needed by reactionsState.js) ======================== */
(function exposeSessionToWindow() {
  // make globals predictable for other modules
  try {
    window.__supabase_session = null;
    window.__supabase_user = null;
  } catch {}

  // 1) prime once on boot
  supabase.auth.getSession().then(({ data }) => {
    try {
      window.__supabase_session = data?.session || null;
      window.__supabase_user    = data?.session?.user || null;
      setSentryUser(data?.session?.user || null);
      console.log('[auth] session primed:', !!window.__supabase_user);
      window.dispatchEvent(new Event('auth:ready'));
    } catch {}
  }).catch(() => {});

  // 2) keep them updated
  supabase.auth.onAuthStateChange((_event, session) => {
    try {
      window.__supabase_session = session || null;
      window.__supabase_user    = session?.user || null;
      setSentryUser(session?.user || null);
      console.log('[auth] session changed:', !!window.__supabase_user);
      window.dispatchEvent(new Event('auth:ready'));
    } catch {}
    
  });
  
})();

export async function createLead(input: CreateLeadInput) {
  const { data, error } = await supabase.functions.invoke("create-lead", {
    body: input,
  });

  if (error) {
    console.error("[lead] create-lead error", error);
    throw error;
  }

  return data as {
    ok: boolean;
    leadId: string;
    publicSlug?: string;
    viewUrl?: string;
  };
}
export async function createQuoteRequest(payload) {
  console.log("[quote] createQuoteRequest payload", payload);

  const { data, error } = await supabase.functions.invoke(
    "create-quote-request",
    {
      body: payload,
    }
  );

  if (error) {
    console.error("[quote] createQuoteRequest error", error);
    throw error;
  }

  return data;
}

// --- Artist profile: upsert shop row for current user ---

export async function upsertArtistProfile(profile) {
  // profile: {
  //   display_name, studio_name, city, region, country,
  //   tagline, style_tags, website, instagram,
  //   accept_pool_requests
  // }

  // 1) Get current user
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    throw new Error("You need to be signed in to create an artist profile.");
  }

  const email = user.email;
  if (!email) {
    throw new Error("Your account must have an email address.");
  }

  // 2) See if a shop already exists for this email
  const { data: existing, error: loadError } = await supabase
    .from("shops")
    .select("*")
    .eq("email", email)
    .maybeSingle();

  if (loadError && loadError.code !== "PGRST116") {
    // PGRST116 = no rows
    console.error("[artistProfile] load error", loadError);
    throw new Error("Could not load your existing studio profile.");
  }

  // Wizard payload uses camelCase; DB columns are snake_case. We accept
  // both shapes here so callers can pass either convention without
  // surprise. Several fields used to be captured by the wizard but
  // silently dropped before reaching the DB (bio, street, postal_code,
  // contactEmail, pool_notification_mode) — those are now persisted.
  const payload = {
    // owner_user_id is required for the new shops RLS — both INSERT and
    // UPDATE policies check `auth.uid() = owner_user_id`. Without it the
    // WITH CHECK clause fails and the write is rejected.
    owner_user_id: user.id,
    // Auth email — used as the row's lookup key. Stays even when the
    // public-facing contact_email is set to something different.
    email,
    name: profile.studio_name || profile.studioName || profile.display_name || profile.displayName || email,
    display_name: profile.display_name || profile.displayName || profile.studio_name || profile.studioName || null,
    tagline: profile.tagline || null,
    bio: profile.bio || null,
    city: profile.city || null,
    region: profile.region || null,
    country: profile.country || null,
    street: profile.street || null,
    postal_code: profile.postal_code || profile.postalCode || null,
    website: profile.website || profile.website_url || null,
    instagram: profile.instagram || profile.instagram_url || null,
    website_url: profile.website || profile.website_url || null,
    instagram_url: profile.instagram || profile.instagram_url || null,
    style_tags: profile.style_tags || profile.styleTags || [],
    styles: profile.style_tags || profile.styleTags || [],
    accept_pool_requests:
      !!(profile.accept_pool_requests ?? profile.acceptPoolRequests),
    pool_notification_mode:
      profile.pool_notification_mode || profile.poolNotificationMode || "instant",
    // Public-facing contact info — separate from auth email. Renders in
    // StudioInfoSection on the public artist page.
    contact_email: profile.contact_email || profile.contactEmail || null,
    phone: profile.phone || null,
    hours: profile.hours ?? null,
    is_active: true,
  };

  let result;
  if (existing) {
    // 3a) Update existing studio
    const { data, error } = await supabase
      .from("shops")
      .update(payload)
      .eq("id", existing.id)
      .select()
      .single();

    if (error || !data) {
      console.error("[artistProfile] update error", error);
      throw new Error("Could not update your studio profile.");
    }
    result = data;
  } else {
    // 3b) Create new studio
    const { data, error } = await supabase
      .from("shops")
      .insert(payload)
      .select()
      .single();

    if (error || !data) {
      console.error("[artistProfile] insert error", error);
      throw new Error("Could not create your studio profile.");
    }
    result = data;
  }

  return result;
}

export async function getOwnShopProfile() {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    return null; // not signed in
  }

  // 1) Preferred: look up by owner_user_id
  let shop = null;

  const { data: byOwner, error: ownerError } = await supabase
    .from("shops")
    .select("*")
    .eq("owner_user_id", user.id)
    .maybeSingle();

  if (ownerError && ownerError.code !== "PGRST116") {
    console.warn("[tattunity] getOwnShopProfile (owner) error", ownerError);
  }

  if (byOwner) {
    shop = byOwner;
  }

  // 2) Fallback: legacy rows that only have email set (no owner_user_id)
  if (!shop && user.email) {
    const { data: byEmail, error: emailError } = await supabase
      .from("shops")
      .select("*")
      .eq("email", user.email)
      .maybeSingle();

    if (emailError && emailError.code !== "PGRST116") {
      console.warn("[tattunity] getOwnShopProfile (email) error", emailError);
    }

    if (byEmail) {
      // 3) Patch that row to attach it to this user for next time
      const { data: patched, error: patchError } = await supabase
        .from("shops")
        .update({ owner_user_id: user.id })
        .eq("id", byEmail.id)
        .select("*")
        .single();

      if (patchError) {
        console.warn("[tattunity] failed to patch owner_user_id", patchError);
        shop = byEmail; // fall back to the unpatched row
      } else {
        shop = patched;
      }
    }
  }

  return shop || null;
}
// ---------- Artist / Studio profile helpers ----------

/**
 * Create or update the current user's studio profile in `shops`.
 * - One shop per owner_user_id (enforced by unique index).
 * - Returns the full row.
 */
export async function upsertOwnShopProfile(input: {
  name: string;
  displayName?: string;
  email?: string;
  contactEmail?: string | null;
  phone?: string | null;
  // jsonb column; v1 wizard sends a free-form string but parseHours on the
  // public page accepts string | string[] | object keyed by day name.
  hours?: unknown;
  city?: string;
  region?: string;
  country?: string;
  street?: string;
  postalCode?: string;
  tagline?: string;
  bio?: string;
  instagram?: string;
  website?: string;
  styles?: string[];
  acceptPoolRequests?: boolean;
  // 'instant'    — email per pool request (default, current behavior)
  // 'inbox_only' — no email, only the in-app inbox + badge
  // The DB constraint also allows 'digest_daily' / 'digest_weekly' but the
  // v1 UI doesn't expose those yet; safe to widen this type later.
  poolNotificationMode?: 'instant' | 'inbox_only';
  bookingLink?: string;
  minRate?: number | null;
}) {
  // 1) get current user
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    console.error("[tattunity] upsertOwnShopProfile: no user", userError);
    throw new Error("You need to be signed in to become a Tattune artist.");
  }

  const ownerId = user.id;

  // 2) Normalise + map fields
  // Accept either `styles` or `styleTags` from callers
const rawStyles = input.styles || (input as any).styleTags || [];
const styles = Array.isArray(rawStyles)
  ? rawStyles.filter(Boolean)
  : [];

  // Simple slug generator from name
  const slug =
    input.name
      ?.toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || null;

  const payload: any = {
    owner_user_id: ownerId,
    name: input.name,
    display_name: input.displayName || input.name,
    // `email` holds the auth email used to look the user up; fall back
    // to the signed-in user's own auth.email so a caller that doesn't
    // pass it can't accidentally null the column. contact_email is the
    // public-facing inbox set in the wizard, kept separate by design.
    email: input.email || user.email || null,
    contact_email: input.contactEmail ?? null,
    phone: input.phone ?? null,
    hours: input.hours ?? null,
    city: input.city || null,
    region: input.region || null,
    country: input.country || null,
    street: input.street || null,             // 👈 new
    postal_code: input.postalCode || null,
    tagline: input.tagline || null,
    bio: input.bio || null,
    instagram: input.instagram || null,
    website: input.website || null,
    styles: styles.length ? styles : null,      // array column 1
    style_tags: styles.length ? styles : null,
    booking_link: input.bookingLink || null,
    min_rate: input.minRate ?? null,
    accept_pool_requests: !!input.acceptPoolRequests,
    // Only persist a value when the caller explicitly provides one — otherwise
    // leave it alone so an undefined input doesn't clobber a user's saved
    // setting back to default.
    ...(input.poolNotificationMode
      ? { pool_notification_mode: input.poolNotificationMode }
      : {}),
    // is_active is the canonical "listed publicly" flag — RLS-enforced
    // and lifecycle-managed (auto-flipped to false when entitlement
    // lapses). The older is_visible column is retired; no code reads
    // from it any more.
    is_active: true,
    slug,
  };

  // 2.5) Address-change detection.
  //
  // The geocode hook below only fires when the post-upsert row has no
  // lat/lng. That's the right behaviour for first-time saves but leaves
  // stale coords stuck whenever an artist later edits their address (moves
  // studios, fixes a typo, changes country). To force a re-geocode, peek at
  // the persisted row and null out lat/lng in the payload if any address
  // component differs from what's currently saved. Pure client-side; if
  // we ever add other writers to public.shops we should reconsider doing
  // this in a Postgres trigger instead.
  const { data: existingShop } = await supabase
    .from("shops")
    .select("street, postal_code, city, region, country, lat, lng")
    .eq("owner_user_id", ownerId)
    .maybeSingle();

  if (existingShop && (existingShop.lat || existingShop.lng)) {
    const addressChanged =
      (existingShop.street       || null) !== payload.street       ||
      (existingShop.postal_code  || null) !== payload.postal_code  ||
      (existingShop.city         || null) !== payload.city         ||
      (existingShop.region       || null) !== payload.region       ||
      (existingShop.country      || null) !== payload.country;

    if (addressChanged) {
      console.log(
        "[geocode] address changed — clearing stale coords to force re-geocode"
      );
      payload.lat = null;
      payload.lng = null;
    }
  }

  // 3) Upsert by owner_user_id (unique index)
  const { data, error } = await supabase
    .from("shops")
    .upsert(payload, { onConflict: "owner_user_id" })
    .select("*")
    .single();

  if (error) {
    console.error("[tattunity] upsertOwnShopProfile error", error);
    throw new Error(error.message || "Could not save your studio profile.");
  }

  // Fire-and-forget geocode if we don't have coords yet.
  //
  // supabase.functions.invoke resolves with { data, error } even on non-2xx
  // responses — it does NOT reject. The .catch branch only fires for network
  // failures or thrown JS errors. So we explicitly inspect res.error here,
  // otherwise upstream issues like "401 unknown API key" or "no address
  // fields" silently disappear and the user sees no coords with no clue why.
  if (!data.lat || !data.lng) {
    supabase.functions
      .invoke("geocode-shop", { body: { shopId: data.id } })
      .then((res) => {
        if (res.error) {
          console.warn(
            "[geocode] failed for shop", data.id,
            "— response:", res.error,
            "— data:", res.data
          );
          return;
        }
        console.log("[geocode] ok for shop", data.id, res.data);
      })
      .catch((err) => {
        console.warn("[geocode] invoke threw", err);
      });
  }

  return data;
}